// src/hooks/useCategories.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { equipmentCategoryService } from '@services/equipmentCategoryService';
import { EquipmentCategory, EquipmentCategoryCreate, EquipmentCategoryUpdate } from '@/types';

const CATEGORY_QUERY_KEY = ['equipment-categories', 'with-count'] as const;

export const useCategories = () => {
  const queryClient = useQueryClient();

  // READ (dedupe + cache)
  const {
    data: categories = [],
    isLoading: loading,
    isFetching,        // útil para spinner sutil en refetch de fondo
    error,
    refetch,           // por si quieres forzar manualmente
  } = useQuery<EquipmentCategory[]>({
    queryKey: CATEGORY_QUERY_KEY,
    queryFn: () => equipmentCategoryService.getAllCategoriesWithCount(),
    staleTime: 5 * 60 * 1000, // 5 min: no refetch en nuevos mounts durante ese tiempo
  });

  // CREATE
  const addCategoryMutation = useMutation({
    mutationFn: (payload: EquipmentCategoryCreate) =>
      equipmentCategoryService.createCategory(payload),
    onSuccess: () => {
      // Revalida la lista
      queryClient.invalidateQueries({ queryKey: CATEGORY_QUERY_KEY });
    },
  });

  // UPDATE
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: EquipmentCategoryUpdate }) =>
      equipmentCategoryService.updateCategory(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_QUERY_KEY });
    },
  });

  // DELETE
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => equipmentCategoryService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_QUERY_KEY });
    },
  });

  return {
    categories,
    loading,
    error: error ? 'Error al cargar las categorías' : null,
    fetching: isFetching,
    refetch,

    // Mutaciones
    addCategory: addCategoryMutation.mutateAsync,
    updateCategory: (id: string, updates: EquipmentCategoryUpdate) =>
      updateCategoryMutation.mutateAsync({ id, updates }),
    deleteCategory: (id: string) => deleteCategoryMutation.mutateAsync(id),

    // Estados de mutación
    addCategoryPending: addCategoryMutation.isPending,
    updateCategoryPending: updateCategoryMutation.isPending,
    deleteCategoryPending: deleteCategoryMutation.isPending,
  };
};
